import { formatUnits, parseUnits } from 'ethers';

/** Format a bigint token amount to a human-readable string with smart precision. */
export function formatTokenAmount(amount: bigint, decimals: number, sigFigs = 6): string {
  if (amount === 0n) return '0';
  const raw = formatUnits(amount, decimals);
  const num = parseFloat(raw);
  if (num === 0) return '0';
  if (num < 0.000001) return num.toExponential(2);
  if (num < 1) return num.toPrecision(sigFigs);
  if (num < 1000) return num.toFixed(4);
  if (num < 1_000_000) return (num / 1).toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (num < 1_000_000_000) return `${(num / 1_000_000).toFixed(2)}M`;
  return `${(num / 1_000_000_000).toFixed(2)}B`;
}

/** Parse a user-input string to bigint with given decimals. Returns null on invalid input. */
export function parseTokenInput(value: string, decimals: number): bigint | null {
  if (!value || value === '' || value === '.') return null;
  try {
    return parseUnits(value, decimals);
  } catch {
    return null;
  }
}

/** Truncate an Ethereum address for display. */
export function truncateAddress(address: string, chars = 4): string {
  if (!address || address.length < 10) return address;
  return `${address.slice(0, chars + 2)}…${address.slice(-chars)}`;
}

/** Format a fee tier (in hundredths of a bip) as a percentage string. */
export function formatFeeTier(fee: number): string {
  return `${(fee / 10000).toFixed(fee % 1000 === 0 ? 2 : fee % 100 === 0 ? 2 : 2)}%`;
}

/** Format a price with smart precision. */
export function formatPrice(price: number, sigFigs = 6): string {
  if (!isFinite(price) || isNaN(price)) return '–';
  if (price === 0) return '0';
  if (price < 0.000001) return price.toExponential(4);
  if (price < 1) return price.toPrecision(sigFigs);
  if (price < 10000) return price.toFixed(4);
  return price.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/** Format a unix timestamp as a date string. */
export function formatTimestamp(ts: number): string {
  return new Date(ts * 1000).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

/** Format ETH value with 4 decimal places. */
export function formatEth(value: bigint): string {
  return formatUnits(value, 18);
}

/** Convert a number 0-100 to basis points (×100). */
export function bpsToPercent(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}

/** Apply slippage tolerance to an amount (returns minimum out). */
export function applySlippageTolerance(amount: bigint, slippageBps: number): bigint {
  return (amount * BigInt(10000 - slippageBps)) / 10000n;
}

/** Apply slippage tolerance to get maximum in. */
export function applySlippageToleranceMax(amount: bigint, slippageBps: number): bigint {
  return (amount * BigInt(10000 + slippageBps)) / 10000n;
}

/** Get deadline as unix timestamp seconds. */
export function getDeadline(minutesFromNow: number): number {
  return Math.floor(Date.now() / 1000) + minutesFromNow * 60;
}

/** Classify error messages from contract reverts into user-friendly strings. */
export function humanizeError(error: unknown): string {
  const msg = error instanceof Error ? error.message : String(error);
  const lower = msg.toLowerCase();
  if (lower.includes('user rejected') || lower.includes('user denied') || lower.includes('rejected the request'))
    return 'Transaction rejected in wallet.';
  if (lower.includes('action_rejected') || msg.includes('4001')) return 'Transaction rejected in wallet.';
  if (lower.includes('nonce too low') || lower.includes('nonce has already been used'))
    return 'Nonce conflict. Reset your wallet activity or wait for the pending transaction.';
  if (lower.includes('replacement transaction underpriced')) return 'Gas too low for a replacement transaction.';
  if (lower.includes('intrinsic gas too low') || lower.includes('out of gas')) return 'Out of gas. Try again with higher gas limit.';
  if (lower.includes('network') && lower.includes('change')) return 'Network was changed in the wallet. Switch back or refresh.';
  if (lower.includes('429') || lower.includes('rate limit') || lower.includes('too many requests'))
    return 'RPC rate limited. Try again in a moment or set a different RPC in settings / env.';
  if (lower.includes('failed to fetch') || lower.includes('networkerror') || lower.includes('load failed'))
    return 'Network request failed. Check your connection or RPC endpoint.';
  if (lower.includes('missing revert data') || lower.includes('execution reverted'))
    return 'Transaction would fail (reverted). Check token pair, pool, and slippage.';
  if (msg.includes('slippage')) return 'Slippage tolerance exceeded. Try increasing slippage.';
  if (msg.includes('expired')) return 'Transaction deadline passed. Please retry.';
  if (msg.includes('no pool')) return 'No pool found for this token pair.';
  if (msg.includes('no input')) return 'No tokens received by pool. Check your approval.';
  if (msg.includes('INSUFFICIENT_BALANCE') || msg.includes('transfer amount exceeds balance')) return 'Insufficient token balance.';
  if (lower.includes('insufficient liquidity')) return 'Insufficient liquidity in pool.';
  if (lower.includes('insufficient')) return 'Insufficient liquidity or balance for this trade.';
  if (msg.includes('too much in')) return 'Would require too much input. Try increasing slippage.';
  if (msg.includes('INSUFFICIENT_ALLOWANCE') || lower.includes('allowance')) return 'Token approval required.';
  if (msg.includes('LOK')) return 'Pool is locked (reentrancy guard). Please retry.';
  if (msg.includes('not initialized')) return 'Pool has not been initialized.';
  if (msg.includes('fee')) return 'Pool creation fee required.';
  if (msg.includes('bad fee')) return 'Unsupported fee tier for this factory.';
  if (msg.includes('exists')) return 'A pool already exists for this pair and fee tier.';
  return msg.length > 200 ? msg.slice(0, 200) + '…' : msg;
}
